import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Table, Button, Badge, Row, Col, Alert } from 'react-bootstrap';
import { fetchPaymentSummary, payInstallment, clearLastCreatedExpense } from '../../redux/slices/paymentsSlice';
import { format } from 'date-fns';

const PaymentDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { paymentSummary, lastCreatedExpense, loading } = useSelector((state) => state.payments);

  useEffect(() => {
    dispatch(fetchPaymentSummary(id));
    return () => { dispatch(clearLastCreatedExpense()); };
  }, [dispatch, id]);

  const handlePayInstallment = async (installmentId) => {
    dispatch(clearLastCreatedExpense());
    await dispatch(payInstallment({ paymentId: id, installmentId }));
    dispatch(fetchPaymentSummary(id));
  };

  if (loading || !paymentSummary) {
    return <Container className="mt-4"><div className="text-center">Cargando...</div></Container>;
  }

  const { payment, paid_installments, pending_installments, paid_amount, pending_amount } = paymentSummary;

  return (
    <Container className="mt-4">
      <Button variant="secondary" onClick={() => navigate('/payments')} className="mb-3">
        Volver
      </Button>

      {lastCreatedExpense && (
        <Alert
          variant="success"
          dismissible
          onClose={() => dispatch(clearLastCreatedExpense())}
          className="mb-3"
        >
          <strong>✓ Cuota pagada.</strong> Se registró automáticamente el egreso{' '}
          <strong>"{lastCreatedExpense.description}"</strong> por{' '}
          <strong>${lastCreatedExpense.amount?.toFixed(2)}</strong> en tu historial de egresos.
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <h2>{payment.title}</h2>
          <Row className="mt-3">
            <Col md={3}>
              <strong>Monto Total:</strong>
              <p className="h4">${payment.total_amount.toFixed(2)}</p>
            </Col>
            <Col md={3}>
              <strong>Cuotas Pagadas:</strong>
              <p className="h4">{paid_installments}/{payment.installments_count}</p>
            </Col>
            <Col md={3}>
              <strong>Monto Pagado:</strong>
              <p className="h4 text-success">${paid_amount.toFixed(2)}</p>
            </Col>
            <Col md={3}>
              <strong>Monto Pendiente:</strong>
              <p className="h4 text-danger">${pending_amount.toFixed(2)}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h4 className="mb-3">Cuotas</h4>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Monto</th>
                <th>Fecha de Vencimiento</th>
                <th>Estado</th>
                <th>Fecha de Pago</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {payment.installments.map((installment) => (
                <tr key={installment.id}>
                  <td>{installment.installment_number}</td>
                  <td>${installment.amount.toFixed(2)}</td>
                  <td>{format(new Date(installment.due_date + 'T00:00:00'), 'dd/MM/yyyy')}</td>
                  <td>
                    {installment.paid
                      ? <Badge bg="success">Pagada</Badge>
                      : <Badge bg="warning">Pendiente</Badge>
                    }
                  </td>
                  <td>
                    {installment.paid_date
                      ? format(new Date(installment.paid_date), 'dd/MM/yyyy HH:mm')
                      : '-'
                    }
                  </td>
                  <td>
                    {!installment.paid && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handlePayInstallment(installment.id)}
                      >
                        Marcar como Pagada — ${installment.amount.toFixed(2)}
                      </Button>
                    )}
                    {installment.paid && installment.expense_id && (
                      <small className="text-muted">Egreso #{installment.expense_id}</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {paymentSummary.paid_history.length > 0 && (
        <Card className="mt-4">
          <Card.Body>
            <h4 className="mb-3">Historial de Pagos</h4>
            <Table striped>
              <thead>
                <tr>
                  <th>Cuota #</th>
                  <th>Monto</th>
                  <th>Fecha de Pago</th>
                  <th>Egreso</th>
                </tr>
              </thead>
              <tbody>
                {paymentSummary.paid_history.map((installment) => (
                  <tr key={installment.id}>
                    <td>{installment.installment_number}</td>
                    <td>${installment.amount.toFixed(2)}</td>
                    <td>{format(new Date(installment.paid_date), 'dd/MM/yyyy HH:mm')}</td>
                    <td>
                      {installment.expense_id
                        ? <small className="text-muted">Egreso #{installment.expense_id}</small>
                        : <small className="text-muted">—</small>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default PaymentDetail;
